/**
 * Língua da interface: a escolhida na conta; sem escolha, a do browser. A extensão usa a
 * mesma (pede-a a /v1/me) para a língua dos guias que grava.
 */
import { matchLocale, type Locale } from '@nolens/i18n';
import type { MeResponse } from '@nolens/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { useMe } from './queries';
import { useSession } from './session';
import { componentsText } from '../i18n/components';

export const browserLocale = (): Locale =>
  matchLocale(
    typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language]),
  );

export const userLocale = (me: Pick<MeResponse, 'locale'> | null | undefined): Locale =>
  me?.locale ?? browserLocale();

/** Guarda a língua na conta (null = voltar a seguir o browser). */
export function useSetLocale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locale: Locale | null) =>
      api<{ locale: Locale | null }>('/v1/me', { method: 'PATCH', body: { locale } }),
    onSuccess: ({ locale }) =>
      qc.setQueryData<MeResponse>(['me'], (me) => (me ? { ...me, locale } : me)),
  });
}

/** Língua da interface agora: a da conta (com sessão) ou a do browser. */
export function useAppLocale(): Locale {
  const { session } = useSession();
  const me = useMe(!!session);
  return session ? userLocale(me.data) : browserLocale();
}

/** Nome de um espaço a mostrar: o pessoal tem o nome na língua da interface. */
export const spaceName = (w: { name: string; kind: 'personal' | 'team' }, l: Locale): string =>
  w.kind === 'personal' ? componentsText(l).personal : w.name;
